<template>
  <div class="employee-work-calendar" data-tour="work-calendar-page">
    <b-alert show variant="light" class="calendar-guidance mb-2">
      <div class="d-flex align-items-start justify-content-between flex-wrap">
        <div class="d-flex align-items-start calendar-guidance-copy">
          <div class="calendar-guidance-icon mr-1">
            <feather-icon icon="CalendarIcon" size="20" />
          </div>
          <div>
            <h5 class="mb-25">{{ $t(`${translationPath}.title`) }}</h5>
            <p class="mb-0 text-muted">
              {{ $t(`${translationPath}.description`) }}
            </p>
          </div>
        </div>
        <tutorial-button
          data-tour="work-calendar-help"
          class="ml-auto mt-1 mt-md-0"
          :label="$t('tutorials.common.viewTutorial')"
          :disabled="loading || scheduledItemsLoading"
          @click="startTutorial"
        />
      </div>
    </b-alert>

    <b-row>
      <b-col cols="12" lg="8">
        <div class="calendar-panel" data-tour="work-calendar-calendar">
          <FullCalendar ref="calendar" :options="calendarOptions" />
          <div v-if="loading" class="calendar-loading-overlay">
            <b-spinner small variant="primary" />
            <span>{{ $t(`${translationPath}.loading`) }}</span>
          </div>
        </div>
      </b-col>

      <b-col cols="12" lg="4" class="mt-2 mt-lg-0">
        <div
          class="schedule-editor h-100"
          data-tour="work-calendar-editor"
        >
          <div class="d-flex align-items-center justify-content-between mb-1">
            <div>
              <small class="text-uppercase text-muted font-weight-bold">
                {{ $t(`${translationPath}.selectedDay`) }}
              </small>
              <h4 class="mb-0">
                {{ selectedDateLabel || $t(`${translationPath}.selectDay`) }}
              </h4>
            </div>
            <b-badge v-if="isEditing" variant="light-primary">
              {{ $t("edit") }}
            </b-badge>
          </div>

          <div v-if="selectedDate">
            <b-form-group
              :label="$t(`${translationPath}.startTime`)"
              label-for="calendarStartTime"
            >
              <b-form-timepicker
                id="calendarStartTime"
                v-model="draft.startTime"
                :placeholder="$t(`${translationPath}.startTime`)"
                :disabled="disabled"
                reset-button
                dropright
              />
            </b-form-group>

            <b-form-group
              :label="$t(`${translationPath}.endTime`)"
              label-for="calendarEndTime"
            >
              <b-form-timepicker
                id="calendarEndTime"
                v-model="draft.endTime"
                :placeholder="$t(`${translationPath}.endTime`)"
                :disabled="disabled"
                reset-button
                dropright
              />
            </b-form-group>

            <b-form-group
              :label="$t(`${translationPath}.notes`)"
              label-for="calendarNotes"
            >
              <b-form-textarea
                id="calendarNotes"
                v-model.trim="draft.notes"
                :placeholder="$t(`${translationPath}.notesPlaceholder`)"
                :disabled="disabled"
                rows="3"
                max-rows="5"
              />
            </b-form-group>

            <b-alert
              v-if="validationMessage"
              show
              variant="danger"
              class="py-75"
            >
              {{ validationMessage }}
            </b-alert>

            <div v-if="!disabled" class="d-flex flex-wrap justify-content-end">
              <b-button
                v-if="isEditing"
                variant="outline-danger"
                class="mr-50 mb-50"
                @click="removeSelectedEntry"
              >
                <feather-icon icon="Trash2Icon" class="mr-25" />
                {{ $t("delete") }}
              </b-button>
              <b-button variant="primary" class="mb-50" @click="saveEntry">
                <feather-icon icon="CheckIcon" class="mr-25" />
                {{ isEditing ? $t("save") : $t("add") }}
              </b-button>
            </div>
          </div>

          <div v-else class="calendar-empty-selection text-center">
            <feather-icon icon="MousePointerIcon" size="34" />
            <p class="mb-0 mt-1 text-muted">
              {{ $t(`${translationPath}.selectDayHelp`) }}
            </p>
          </div>
        </div>
      </b-col>
    </b-row>

    <div class="scheduled-days mt-2" data-tour="work-calendar-scheduled">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <div>
          <h4 class="mb-25">{{ $t(`${translationPath}.scheduledDays`) }}</h4>
          <small class="text-muted">
            {{ $t(`${translationPath}.scheduledDaysDescription`) }}
          </small>
        </div>
        <b-badge variant="light-primary" pill>
          {{ displayScheduledItemsTotal }}
        </b-badge>
      </div>

      <b-table
        v-if="displayScheduledEntries.length"
        responsive
        bordered
        hover
        :items="displayScheduledEntries"
        :fields="calendarFields"
        class="mb-0"
      >
        <template #cell(workDate)="data">
          <span class="font-weight-bold">{{
            formatDate(data.item.workDate)
          }}</span>
        </template>
        <template #cell(startTime)="data">
          {{ formatTime(data.item.startTime) }}
        </template>
        <template #cell(endTime)="data">
          {{ formatTime(data.item.endTime) }}
        </template>
        <template #cell(notes)="data">
          <span :class="{ 'text-muted': !data.item.notes }">
            {{ data.item.notes || $t(`${translationPath}.withoutNotes`) }}
          </span>
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="flat-primary"
            :aria-label="$t('edit')"
            @click="selectEntry(data.item)"
          >
            <feather-icon icon="EditIcon" />
          </b-button>
          <b-button
            v-if="!disabled"
            size="sm"
            variant="flat-danger"
            :aria-label="$t('delete')"
            @click="removeEntry(data.item)"
          >
            <feather-icon icon="Trash2Icon" />
          </b-button>
        </template>
      </b-table>

      <div
        v-else-if="scheduledItemsLoading"
        class="scheduled-days-empty text-center"
      >
        <b-spinner variant="primary" />
        <p class="mb-0 mt-75 text-muted">
          {{ $t(`${translationPath}.loadingScheduled`) }}
        </p>
      </div>

      <div v-else class="scheduled-days-empty text-center">
        <feather-icon icon="CalendarIcon" size="32" />
        <p class="mb-0 mt-75 text-muted">
          {{ scheduledItemsEmptyMessage }}
        </p>
      </div>

      <div
        v-if="
          remoteScheduledItems &&
          scheduledItemsLoaded &&
          scheduledItemsTotal > 0
        "
        class="d-flex justify-content-center mt-1"
        data-tour="work-calendar-pagination"
      >
        <b-pagination
          :value="scheduledItemsPage"
          :total-rows="scheduledItemsTotal"
          :per-page="scheduledItemsPerPage"
          :disabled="scheduledItemsLoading"
          first-number
          last-number
          @input="$emit('scheduled-page-change', $event)"
        />
      </div>

    </div>

    <app-tour
      :name="tutorialName"
      :steps="tutorialSteps"
      :options="tutorialOptions"
      :callbacks="tutorialCallbacks"
      @target-not-found="onTutorialTargetNotFound"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCol,
  BFormGroup,
  BFormTextarea,
  BFormTimepicker,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from "bootstrap-vue";
import FullCalendar from "@fullcalendar/vue";
import esLocale from "@fullcalendar/core/locales/es";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";
import AppTour from "@core/components/app-tour/AppTour.vue";
import TutorialButton from "@core/components/app-tour/TutorialButton.vue";
import { buildEmployeeWorkCalendarTour } from "@/tutorials/employees";

const emptyDraft = () => ({
  workDate: null,
  startTime: null,
  endTime: null,
  notes: "",
});

export default {
  name: "EmployeeWorkCalendar",
  components: {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BFormGroup,
    BFormTextarea,
    BFormTimepicker,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    FullCalendar,
    AppTour,
    TutorialButton,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    scheduledItems: {
      type: Array,
      default: () => [],
    },
    remoteScheduledItems: {
      type: Boolean,
      default: false,
    },
    scheduledItemsLoading: {
      type: Boolean,
      default: false,
    },
    scheduledItemsLoaded: {
      type: Boolean,
      default: false,
    },
    scheduledItemsPage: {
      type: Number,
      default: 1,
    },
    scheduledItemsPerPage: {
      type: Number,
      default: 5,
    },
    scheduledItemsTotal: {
      type: Number,
      default: 0,
    },
  },
  data: () => ({
    selectedDate: null,
    selectedEntryKey: null,
    draft: emptyDraft(),
    validationMessage: "",
    translationPath: "pages.employees.workCalendar",
    calendarResizeObserver: null,
    calendarResizeTimer: null,
    lastObservedWidth: 0,
    tutorialName: "employeeWorkCalendarTour",
  }),
  computed: {
    tutorialSteps() {
      return buildEmployeeWorkCalendarTour({
        t: this.$t.bind(this),
        disabled: this.disabled,
        hasSelectedDate: Boolean(this.selectedDate),
        hasScheduledItems: this.displayScheduledEntries.length > 0,
        hasPagination:
          this.remoteScheduledItems &&
          this.scheduledItemsLoaded &&
          this.scheduledItemsTotal > this.scheduledItemsPerPage,
      });
    },
    tutorialOptions() {
      return {
        highlight: true,
        enableScrolling: true,
        useKeyboardNavigation: true,
        startTimeout: 150,
        stopOnTargetNotFound: true,
      };
    },
    tutorialCallbacks() {
      return {
        onFinish: this.onTutorialFinish,
      };
    },
    entries() {
      return Array.isArray(this.value) ? this.value : [];
    },
    sortedEntries() {
      return [...this.entries].sort((left, right) =>
        this.entryDate(left).localeCompare(this.entryDate(right)),
      );
    },
    displayScheduledEntries() {
      const items = this.remoteScheduledItems
        ? this.scheduledItems
        : this.sortedEntries;
      return [...items].sort((left, right) =>
        this.entryDate(left).localeCompare(this.entryDate(right)),
      );
    },
    displayScheduledItemsTotal() {
      return this.remoteScheduledItems
        ? this.scheduledItemsTotal
        : this.sortedEntries.length;
    },
    scheduledItemsEmptyMessage() {
      if (!this.remoteScheduledItems) {
        return this.$t(`${this.translationPath}.empty`);
      }
      return this.$t(
        `${this.translationPath}.${
          this.scheduledItemsLoaded ? "emptyScheduled" : "selectScheduledDay"
        }`,
      );
    },
    selectedDateLabel() {
      if (!this.selectedDate) return "";
      return moment(this.selectedDate, "YYYY-MM-DD").format("DD/MM/YYYY");
    },
    isEditing() {
      return this.selectedEntryIndex !== -1;
    },
    selectedEntryIndex() {
      if (!this.selectedEntryKey) return -1;
      return this.entries.findIndex(
        (entry, index) => this.entryKey(entry, index) === this.selectedEntryKey,
      );
    },
    calendarFields() {
      const fields = [
        {
          key: "workDate",
          label: this.$t(`${this.translationPath}.date`),
        },
        {
          key: "startTime",
          label: this.$t(`${this.translationPath}.startTime`),
        },
        {
          key: "endTime",
          label: this.$t(`${this.translationPath}.endTime`),
        },
        { key: "notes", label: this.$t(`${this.translationPath}.notes`) },
      ];

      fields.push({
        key: "actions",
        label: this.$t("pages.employees.table.tableHeader.action"),
        class: "text-center text-nowrap",
      });
      return fields;
    },
    calendarEvents() {
      return this.entries
        .filter((entry) => this.entryDate(entry))
        .map((entry, index) => ({
          id: this.entryKey(entry, index),
          title: `${this.shortTime(entry.startTime)} - ${this.shortTime(
            entry.endTime,
          )}`,
          start: this.entryDate(entry),
          allDay: true,
          backgroundColor: "#7367f0",
          borderColor: "#7367f0",
        }));
    },
    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin],
        locales: [esLocale],
        locale: String(this.$i18n.locale || "")
          .toLowerCase()
          .startsWith("es")
          ? "es"
          : "en",
        firstDay: 1,
        initialView: "dayGridMonth",
        height: "auto",
        fixedWeekCount: false,
        showNonCurrentDates: true,
        headerToolbar: {
          start: "prev,next today",
          center: "title",
          end: "",
        },
        buttonText: {
          today: this.$t(`${this.translationPath}.today`),
        },
        events: this.calendarEvents,
        datesSet: this.onDatesSet,
        dateClick: this.onDateClick,
        eventClick: this.onEventClick,
        eventDisplay: "block",
        dayCellClassNames: (info) =>
          moment(info.date).format("YYYY-MM-DD") === this.selectedDate
            ? ["employee-work-calendar-selected-day"]
            : [],
      };
    },
  },
  mounted() {
    this.observeCalendarSize();
    window.addEventListener("resize", this.refreshCalendarSize);
  },
  beforeDestroy() {
    if (this.calendarResizeObserver) {
      this.calendarResizeObserver.disconnect();
      this.calendarResizeObserver = null;
    }
    clearTimeout(this.calendarResizeTimer);
    window.removeEventListener("resize", this.refreshCalendarSize);
  },
  methods: {
    async startTutorial() {
      if (this.loading || this.scheduledItemsLoading) return;

      await this.$nextTick();
      const tour = this.$tours[this.tutorialName];
      if (!tour || !this.tutorialSteps.length) return;

      const allTargetsExist = this.tutorialSteps.every((step) =>
        document.querySelector(step.target),
      );

      if (!allTargetsExist) {
        this.onTutorialTargetNotFound();
        return;
      }

      try {
        await tour.start();
      } catch (error) {
        this.onTutorialTargetNotFound();
      }
    },
    onTutorialFinish() {
      const idUser =
        this.$store.state.appConfig.sessionContext?.idUser || "user";
      localStorage.setItem(
        `sofia:tutorial:${idUser}:employees:work-calendar:v1`,
        "completed",
      );
    },
    onTutorialTargetNotFound() {
      this.$bvToast.toast(
        this.$t("tutorials.common.unavailableContent"),
        {
          title: this.$t("tutorials.common.unavailableTitle"),
          variant: "warning",
          solid: true,
        },
      );
    },
    observeCalendarSize() {
      if (typeof ResizeObserver !== "undefined") {
        this.calendarResizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect?.width || 0;
          if (width > 0 && Math.abs(width - this.lastObservedWidth) > 1) {
            this.lastObservedWidth = width;
            this.refreshCalendarSize();
          }
        });
        this.calendarResizeObserver.observe(this.$el);
      }

      this.refreshCalendarSize();
    },
    refreshCalendarSize() {
      clearTimeout(this.calendarResizeTimer);
      this.calendarResizeTimer = setTimeout(() => {
        this.$nextTick(() => {
          if (!this.$el || this.$el.offsetWidth === 0) return;
          const calendarApi = this.$refs.calendar?.getApi?.();
          if (calendarApi) calendarApi.updateSize();
        });
      }, 80);
    },
    entryDate(entry) {
      if (!entry || !entry.workDate) return "";
      const rawDate = String(entry.workDate);
      const datePart = rawDate.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
      if (datePart) return datePart;

      const date = moment(rawDate);
      return date.isValid() ? date.format("YYYY-MM-DD") : "";
    },
    entryKey(entry, index = 0) {
      if (entry?.idWorkCalendar) {
        return `work-calendar-id-${entry.idWorkCalendar}`;
      }
      if (entry?.uuid) return `work-calendar-uuid-${entry.uuid}`;
      return `work-calendar-date-${this.entryDate(entry)}-${index}`;
    },
    createUuid(date) {
      return `${date}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    },
    onDatesSet(info) {
      this.$emit("range-change", {
        start: info.view?.currentStart || info.start,
        end: info.view?.currentEnd || info.end,
      });
    },
    onDateClick(info) {
      const existingEntry = this.entries.find(
        (entry) => this.entryDate(entry) === info.dateStr,
      );

      if (this.disabled && !existingEntry) return;
      if (existingEntry) {
        this.$emit("day-consult", existingEntry);
        this.selectEntry(existingEntry);
      } else {
        this.prepareNewEntry(info.dateStr);
      }
    },
    onEventClick(info) {
      const entry = this.entries.find(
        (item, index) => this.entryKey(item, index) === info.event.id,
      );
      if (entry) {
        this.$emit("day-consult", entry);
        this.selectEntry(entry);
      }
    },
    prepareNewEntry(date) {
      this.selectedDate = date;
      this.selectedEntryKey = null;
      this.draft = {
        ...emptyDraft(),
        workDate: `${date}T00:00:00`,
      };
      this.validationMessage = "";
    },
    selectEntry(entry) {
      const sourceIndex = this.entries.indexOf(entry);
      const selectedKey = this.entryKey(entry, sourceIndex);
      const index = this.entries.findIndex(
        (item, itemIndex) =>
          this.entryKey(item, itemIndex) === selectedKey,
      );
      if (index === -1) return;

      this.selectedDate = this.entryDate(entry);
      this.selectedEntryKey = selectedKey;
      this.draft = {
        ...this.entries[index],
        workDate: this.entries[index].workDate,
        notes: this.entries[index].notes || "",
      };
      this.validationMessage = "";
    },
    saveEntry() {
      this.validationMessage = "";
      if (!this.selectedDate || !this.draft.startTime || !this.draft.endTime) {
        this.validationMessage = this.$t(
          `${this.translationPath}.requiredFields`,
        );
        return;
      }

      if (this.draft.startTime >= this.draft.endTime) {
        this.validationMessage = this.$t(
          `${this.translationPath}.invalidTimeRange`,
        );
        return;
      }

      const duplicateIndex = this.entries.findIndex(
        (entry, index) =>
          this.entryDate(entry) === this.selectedDate &&
          index !== this.selectedEntryIndex,
      );
      if (duplicateIndex !== -1) {
        this.validationMessage = this.$t(
          `${this.translationPath}.dateAlreadyExists`,
        );
        return;
      }

      const entry = {
        ...this.draft,
        workDate: `${this.selectedDate}T00:00:00`,
        startTime: this.draft.startTime,
        endTime: this.draft.endTime,
        notes: this.draft.notes || null,
      };

      const updatedEntries = [...this.entries];
      const changeType = this.selectedEntryIndex === -1 ? "create" : "update";
      if (changeType === "create") {
        entry.uuid = this.createUuid(this.selectedDate);
        updatedEntries.push(entry);
      } else {
        updatedEntries.splice(this.selectedEntryIndex, 1, entry);
      }

      this.$emit("input", updatedEntries);
      this.$emit("calendar-change", { type: changeType, entry });
      this.$nextTick(() => {
        const savedKey = this.entryKey(entry, this.selectedEntryIndex);
        const savedEntry = updatedEntries.find(
          (item, index) => this.entryKey(item, index) === savedKey,
        );
        if (savedEntry) this.selectEntry(savedEntry);
      });
    },
    removeSelectedEntry() {
      if (this.selectedEntryIndex === -1) return;
      this.removeEntry(this.entries[this.selectedEntryIndex]);
    },
    removeEntry(entry) {
      const sourceIndex = this.entries.indexOf(entry);
      const entryKey = this.entryKey(entry, sourceIndex);
      const index = this.entries.findIndex(
        (item, itemIndex) => this.entryKey(item, itemIndex) === entryKey,
      );
      if (index === -1) return;
      const removedEntry = this.entries[index];
      const updatedEntries = this.entries.filter(
        (item, itemIndex) => itemIndex !== index,
      );
      this.$emit("input", updatedEntries);
      this.$emit("calendar-change", { type: "delete", entry: removedEntry });
      this.resetSelection();
    },
    resetSelection() {
      this.selectedDate = null;
      this.selectedEntryKey = null;
      this.draft = emptyDraft();
      this.validationMessage = "";
    },
    formatDate(date) {
      const normalizedDate = this.entryDate({ workDate: date });
      const value = moment(normalizedDate, "YYYY-MM-DD", true);
      return value.isValid() ? value.format("DD/MM/YYYY") : "—";
    },
    formatTime(time) {
      if (!time) return "—";
      const value = moment(time, "HH:mm:ss");
      return value.isValid() ? value.format("hh:mm A") : time;
    },
    shortTime(time) {
      return time ? String(time).slice(0, 5) : "--:--";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/apps/calendar.scss";

.employee-work-calendar {
  .calendar-guidance {
    border: 1px solid rgba(115, 103, 240, 0.16);
    background: rgba(115, 103, 240, 0.05);
  }

  .calendar-guidance-icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 42px;
    color: #7367f0;
    background: rgba(115, 103, 240, 0.12);
  }

  .calendar-panel,
  .schedule-editor,
  .scheduled-days {
    border: 1px solid #ebe9f1;
    border-radius: 0.6rem;
    background: #fff;
    box-shadow: 0 4px 18px rgba(34, 41, 47, 0.04);
  }

  .calendar-panel {
    position: relative;
  }

  .calendar-loading-overlay {
    position: absolute;
    inset: 0;
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;
    color: #5e5873;
    font-weight: 600;
    border-radius: 0.6rem;
    background: rgba(255, 255, 255, 0.86);
  }

  .calendar-panel,
  .schedule-editor,
  .scheduled-days {
    padding: 1.25rem;
  }

  .schedule-editor {
    min-height: 390px;
  }

  .calendar-empty-selection,
  .scheduled-days-empty {
    color: #b9b9c3;
    border: 1px dashed #d8d6de;
    border-radius: 0.5rem;
    padding: 2.5rem 1rem;
    background: #fafafa;
  }

  .fc .fc-toolbar-title {
    font-size: 1.2rem;
  }

  .fc .fc-daygrid-day.fc-day-today {
    background: rgba(115, 103, 240, 0.07);
  }

  .fc .employee-work-calendar-selected-day {
    background: rgba(40, 199, 111, 0.1);
    box-shadow: inset 0 0 0 2px rgba(40, 199, 111, 0.35);
  }

  .fc .fc-daygrid-day-frame {
    cursor: pointer;
  }

  .fc .fc-event {
    cursor: pointer;
  }
}
</style>
